import { type Component, type ComponentProps, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Folder, Inbox, RefreshCw, Send as SendIcon, Settings, Star, Trash2 } from "@fleet/icons";

import { cn } from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Select } from "@/components/ui/select";
import { CounterBadge } from "@/components/ui/counter-badge";
import type { MailAccountView, MailFolder } from "../api";

/** Well-known IMAP folder → icon, so the rail reads like a real mail client. */
function folderIcon(name: string): Component<ComponentProps<"svg">> {
  const lower = name.toLowerCase();
  if (lower === "inbox") {
    return Inbox;
  }
  if (lower.includes("sent")) {
    return SendIcon;
  }
  if (lower.includes("trash") || lower.includes("deleted")) {
    return Trash2;
  }
  if (lower.includes("flag") || lower.includes("star")) {
    return Star;
  }
  return Folder;
}

interface FolderRailProps {
  accounts: MailAccountView[];
  activeAccountId: string | undefined;
  onAccountChange: (id: string) => void;
  folders: MailFolder[];
  activeFolder: string;
  onFolderChange: (name: string) => void;
  onSync: () => void;
  syncing: boolean;
  onOpenSettings: () => void;
}

export function FolderRail(props: FolderRailProps) {
  const { t } = useI18n();

  const accountOptions = () =>
    props.accounts.map((a) => ({ value: a.id, label: a.displayName?.trim() || a.email }));

  return (
    <aside
      class="flex w-60 shrink-0 flex-col gap-2 border-r border-border/40 bg-card/10 p-3"
      data-testid="mail-folder-rail"
    >
      <div class="flex items-center gap-2">
        <div class="min-w-0 flex-1">
          <Show
            when={props.accounts.length > 0}
            fallback={
              <span class="block truncate px-1 text-sm text-muted-foreground">
                {t(msg`No account`)}
              </span>
            }
          >
            <Select<string>
              aria-label={t(msg`Active account`)}
              value={props.activeAccountId}
              onChange={props.onAccountChange}
              options={accountOptions()}
              triggerClass="h-9"
            />
          </Show>
        </div>
        <button
          type="button"
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/10 hover:text-foreground disabled:opacity-50"
          onClick={() => props.onSync()}
          disabled={props.syncing || !props.activeAccountId}
          aria-label={t(msg`Sync now`)}
          data-testid="mail-sync-button"
        >
          <RefreshCw class={cn("h-4 w-4", props.syncing && "animate-spin")} />
        </button>
      </div>

      <nav
        class="flex flex-1 flex-col gap-0.5 overflow-y-auto scrollbar-hide"
        aria-label={t(msg`Folders`)}
      >
        <For each={props.folders}>
          {(folder) => {
            const Icon = folderIcon(folder.name);
            const active = () => folder.name === props.activeFolder;
            return (
              <button
                type="button"
                onClick={() => props.onFolderChange(folder.name)}
                data-testid="mail-folder-item"
                data-folder={folder.name}
                aria-current={active() ? "true" : undefined}
                class={cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                  active()
                    ? "bg-accent/15 text-foreground font-medium"
                    : "text-muted-foreground/90 hover:bg-accent/5 hover:text-foreground",
                )}
              >
                <Icon class="h-4 w-4 shrink-0" />
                <span class="min-w-0 flex-1 truncate text-left">{folder.name}</span>
                <Show when={folder.unread > 0}>
                  <CounterBadge count={folder.unread} />
                </Show>
              </button>
            );
          }}
        </For>
        <Show when={props.folders.length === 0 && props.activeAccountId}>
          <p class="px-3 py-2 text-xs text-muted-foreground/70">
            {t(msg`No folders yet — sync to load them.`)}
          </p>
        </Show>
      </nav>

      <button
        type="button"
        onClick={() => props.onOpenSettings()}
        class="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground/90 hover:bg-accent/5 hover:text-foreground"
        data-testid="mail-settings-link"
      >
        <Settings class="h-4 w-4 shrink-0" />
        <span>{t(msg`Mail settings`)}</span>
      </button>
    </aside>
  );
}
